import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';

import { calculateAmericanPrice, addAmericanOdds } from '~shared/components/sport-event/utils';
import {
    WEBSOCKET_EVENT_PRICE_UPDATE,
    WEBSOCKET_EVENT_EVENTS_RESULTS,
    WEBSOCKET_EVENT_EVENT_OFF,
    WEBSOCKET_EVENT_INPLAY_UPDATE,
    WEBSOCKET_EVENT_RESCHEDULED,
    WEBSOCKET_EVENT_INPLAY_START,
} from './constants';
import { mergeMarkets, getMarketBetable } from '~shared/utils/market-utils';
import { selectionArrayToObject } from '~shared/utils/selections-utils';

const minusOne = -1;

/**
 * Create/update the common store structure.
 * @param {Object} data From FE/PHP
 * @param {Object} state The current commonStore state
 * @return {Object} New commonStore state
 */
export const createCommonStoreStructure = (data, state) => {
    return merge(state, data);
};

/**
 * Check websocket packet for events / markets / selections that need to be updated or removed
 * @param {Object} wsData From FE/PHP
 * @param {Object} state The current commonStore state (events)
 * @returns {Object} New commonStore state
 */
export const websocketUpdater = (wsData, state) => {
    let newState = {};

    /**
     * Add action handlers to this code block that return a new state
     */
    switch (wsData.action) {
        case WEBSOCKET_EVENT_RESCHEDULED: {
            newState = processRescheduledWsEvent(wsData, state);

            return newState;
        }
        case WEBSOCKET_EVENT_PRICE_UPDATE: {
            /*
            {
            "action":"price_update",
            "supplier":null,
            "events":[
                {
                    "tradable":true,
                    "suspended":null,
                    "active":true,
                    "markets":[
                        {
                        "name":"Match Result",
                        "selections":{
                            "286164":{
                                "fraction_price":"1\/5",
                                "decimal_price":1.2,
                                "tradable":true,
                                "sp_only":false,
                                "suspended":null,
                                "active":true,
                                "selection_type_id":1,
                                "outcome":null,
                                "price":1.2,
                                "extra_info":null
                            }
                        },
                        "tradable":true,
                        "restrict_cashout":false,
                        "active":true,
                        "id":8854,
                        "extra_info":null
                        }
                    ],
                    "id":2136
                }
            ]
            }
            */
            if (!isUndefined(wsData.events)) {
                newState = updateCommonStoreStructure(wsData.events, state);

                return newState;
            }

            break;
        }
        case WEBSOCKET_EVENT_INPLAY_UPDATE: {
            /*
             * will also suspend events and markets (as well as remove in case of match_status)
             */

            if (!isUndefined(wsData.events) || !isUndefined(wsData.event)) {
                let eventData = wsData.events || [wsData.event];

                newState = updateCommonStoreStructure(eventData, state);

                return newState;
            }

            break;
        }
        case WEBSOCKET_EVENT_INPLAY_START: {
            const preplayEventsToRemove = parseObjectForEventIds(wsData);

            newState = addNewInplayEvents(wsData.extra_events_info, wsData.inplay_events, state);
            newState = removeStaleEventsFromCommonStoreStructure(preplayEventsToRemove, newState);

            return newState;
        }
        default: {
        }
    }

    /**
     * Add handlers to the following switch block that DO NOT return a new state, but
     * populate the following array. They are processed at the end of the block.
     */
    let eventsToRemove = [];
    switch (wsData.action) {
        /**
         * Preplay events off become 'new' events in the BE. We remove them from the
         * Store. All following include an array of event ids in the websocket
         * update (wsData.events) that can be removed from the common store.
         */
        case WEBSOCKET_EVENT_INPLAY_START:
        case WEBSOCKET_EVENT_EVENT_OFF:
        case WEBSOCKET_EVENT_EVENTS_RESULTS: {
            eventsToRemove = parseObjectForEventIds(wsData);

            break;
        }
        default: {
            break;
        }
    }

    newState = removeStaleEventsFromCommonStoreStructure(eventsToRemove, state);

    return newState;
};

export const processRescheduledWsEvent = (wsData, commonDataEvents) => {
    /*
    {
        'action': 'rescheduled',
        'current_time': '2018-03-01 15:00:00',
        'events': [{
            'rescheduled_time': '2018-04-01 15:00:00',
            'id': 67
        }]
    }
    */
    if (!isUndefined(wsData.events)) {
        wsData.events.forEach(event => {
            if (!isUndefined(commonDataEvents[event.id])) {
                commonDataEvents[event.id].scheduled_start = event.rescheduled_time;
            }
        });
    }

    return { ...commonDataEvents };

};

/**
 * Parse websocket payload for an 'events' property and construct an array
 * of event ids
 * @param {Object} wsData the websocket payload
 * @returns {Array} id's of events
 */
export const parseObjectForEventIds = wsData => {
    /*
    {
        "action": "events_results",
        "events": [604630]
    }
    */
    /*
    {
        "action": "event_off",
        "inplay_events": {},
        "events": [604424],
        "inplay_live": {}
    }
    */
    /*
    {
        'action':'inplay_start',
        'events':[
            1,
            2083,
            2108,
            99999999999
        ],
        'inplay_events':{
            1:5,
            2:6
        },
        'inplay_live':{
        },
        'extra_events_info':[
        ]
    }
    */
   const eventIds = [];
    if (!isUndefined(wsData.events)) {
        wsData.events.forEach(eventId => {
            eventIds.push(eventId);
        });
    }

    return eventIds;
};

/**
 * Remove stale events / markets / selections from the common store
 * @param {Object} eventsToRemove array of event Id's
 * @param {Object} state existing state
 * @returns {Object} new state
 */
export const removeStaleEventsFromCommonStoreStructure = (eventsToRemove, state) => {
    const updatedState = { ...state };

    if (eventsToRemove.length) {
        eventsToRemove.forEach(eventId => {
            if (!isUndefined(updatedState[eventId])) {
                delete updatedState[eventId];
            }
        });
    }

    return updatedState;
};

const completeStatus = [
    'canceled', 'abandoned', 'walkover', 'retired', 'ended', 'manually_deactivated',
];

export const eventStatusIsComplete = event => {
    if (isUndefined(event.match_status)) {
        return false;
    }

    return completeStatus.indexOf(event.match_status.toLowerCase()) !== minusOne;
};

/**
 * Update the common store structure.
 *
 * @param {Object} events Events data from web socket updates
 * @param {Object} state The current state (events)
 * @return {Object} New state
 */
export const updateCommonStoreStructure = (events = [], state) => {
    const updatedState = { ...state };
    events.forEach(event => {
        const { id: eventId, is_inplay: isInplay = false} = event;
        if (eventStatusIsComplete(event)) {
            delete updatedState[eventId];
        } else if (!isUndefined(state[eventId])) {
            let eventBetable = state[eventId].betable;
            if (!isUndefined(event.betable)) {
                eventBetable = event.betable;
            } else if(!isUndefined(event.suspended)) {
                eventBetable = !event.suspended;
            } else if (!isUndefined(event.tradable)) {
                eventBetable = event.tradable;
            }

            updatedState[eventId] = {
                ...state[eventId],
                betable: eventBetable,
            };

            if (!isUndefined(event.markets)) {
                if (!isUndefined(updatedState[eventId].markets)) {
                    updatedState[eventId].markets = {
                        ...updatedState[eventId].markets,
                    };
                }
                mergeMarkets(event.markets)
                .forEach(market => {
                    const marketId = market.id;
                    if (isUndefined(state[eventId].markets[marketId])) {
                        // we should not be updating any markets
                        // that do not already exist in the store
                        return;
                    }
                    const marketBetable = getMarketBetable(market);

                    updatedState[eventId].markets[marketId] = {
                        ...state[eventId].markets[marketId],
                        ...updatedState[eventId].markets[marketId],
                        betable: marketBetable,
                    };

                    const { default_line_selections: defaultLineChange = [], selections: marketSelections = {} } = market;
                    const defaultLineHasChanged = Boolean(defaultLineChange.length);
                    if (defaultLineHasChanged) {
                        // default_line_selections will be empty except when the default line changes.
                        // if there is selecion id's in 'default_line_selections' then those selections
                        // will always be included in the same packet.
                        updatedState[eventId].markets[marketId].default_line_selections = defaultLineChange;
                        defaultLineChange.forEach(selectionId => {
                            if (isUndefined(updatedState[eventId].markets[marketId].selections)) {
                                updatedState[eventId].markets[marketId].selections = {};
                            }
                            if (isUndefined(state[eventId].markets[marketId].selections)) {
                                state[eventId].markets[marketId].selections = {};
                            }
                            if (isUndefined(state[eventId].markets[marketId].selections[selectionId])) {
                                const selection = marketSelections[selectionId];
                                if(selection) {
                                    // if the selection does not already exist in the store add it.
                                    // 'id' and 'special_odds_value' are attributes of selections when
                                    // they come from the normal endpoint but not in selections that come from WS
                                    // so this adds them.
                                    state[eventId].markets[marketId].selections[selectionId] = {
                                        id: selectionId,
                                        special_odds_value: selection.specialoddsvalue,
                                        ...selection,
                                    };
                                }
                            }
                        });
                    }

                    if (!isUndefined(market.selections)) {
                        if (!isUndefined(updatedState[eventId].markets[marketId].selections)) {
                            updatedState[eventId].markets[marketId].selections = {
                                ...updatedState[eventId].markets[marketId].selections,
                            };
                        }

                        if(isInplay && market.selections.length) {
                            // market.selections inplay updates is formatted as an array.
                            // this changes it to match the preplay payload.
                            market.selections = selectionArrayToObject(market.selections);
                        }
                        Object.keys(market.selections).forEach(selectionId => {
                            const selection = market.selections[selectionId];
                            let selectionBetable;
                            if (!isUndefined(selection.betable)) {
                                selectionBetable = selection.betable;
                            } else {
                                selectionBetable = !selection.suspended;
                            }

                            if (!isUndefined(updatedState[eventId].markets[marketId].selections)
                                && !isUndefined(updatedState[eventId].markets[marketId].selections[selectionId])
                            ) {
                                updatedState[eventId].markets[marketId].selections[selectionId] = {
                                    ...state[eventId].markets[marketId].selections[selectionId],
                                    decimal_price: selection.decimal_price,
                                    fraction_price: selection.fraction_price,
                                    american_price: calculateAmericanPrice(selection.decimal_price),
                                    betable: selectionBetable,
                                    suspended: !selectionBetable, // Retrocompatibility
                                };
                            }
                        });
                    }
                });
            }
        }
    });

    return updatedState;
};

export const addNewInplayEvents = (events, preplayMapping, state) => {
    const mappedEvents = Object.keys(preplayMapping).reduce((mappedEvents, preplayEventId) => {
        const preplayEvent = state[preplayEventId];

        if (isUndefined(preplayEvent)) {
            return mappedEvents;
        }

        const inplayEventId = preplayMapping[preplayEventId];
        const inplayEvent = events[inplayEventId];

        if (isUndefined(inplayEvent) || isUndefined(inplayEvent.selections)) {
            return mappedEvents;
        }

        const selections = inplayEvent.selections.reduce((selections, selection) => {
            const betable = !isUndefined(selection.betable)
                ? selection.betable
                : !selection.suspended;

            selections[selection.id] = {
                ...selection,
                extra_info: {
                    ...selection.extra_info,
                    participant_id: selection.id,
                },
                betable,
            };

            return selections;
        }, {});

        mappedEvents[inplayEventId] = {
            id: inplayEventId,
            name: inplayEvent.name,
            betable: !inplayEvent.suspended,
            is_inplay: true,
            is_outright: preplayEvent.is_outright,
            market_order: preplayEvent.market_order,
            // @TODO use scheduled_start
            scheduled_date: preplayEvent.scheduled_date,
            scheduled_start: preplayEvent.scheduled_start,
            markets: {
                [inplayEvent.market_id]: {
                    id: inplayEvent.market_id,
                    betable: !inplayEvent.market_suspended,
                    ew_terms: preplayEvent.ew_terms,
                    selections,
                },
            },
        };

        return mappedEvents;
    }, {});

    return {...state, ...mappedEvents};
};


export const addPriceToEventsSelections = (events = {}) => {
    if (events) {
        Object.keys(events).forEach((eventId) => {
            if (events[eventId] && events[eventId].markets) {
                Object.keys(events[eventId].markets).forEach((marketId) => {
                    if (events[eventId].markets[marketId] && events[eventId].markets[marketId].selections) {
                        Object.keys(events[eventId].markets[marketId].selections).forEach((selectionId) => {
                            addAmericanOdds(events[eventId].markets[marketId].selections[selectionId]);
                        });
                    }
                });
            }
        });
    }
};
